"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignUpButton,
  UserButton,
} from "@clerk/nextjs"
import { cn } from "@/lib/utils"
import { Menu } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { calculateContrastRatio } from "@/lib/color-generation/utils/wcagContrast"

const navItems = [
  { name: "Home", href: "/" },
  { name: "Masterminds", href: "/mastermind" },
  { name: "Chat", href: "/chat" },
  { name: "Tools", href: "/tools" },
  { name: "Agent", href: "/agent", hidden: true },
  { name: "Backrooms", href: "/backrooms" },
]

interface MastermindPaletteData {
  palette: string[];
  personaAssignments: { [personaId: string]: number };
  userColorIndex: number;
  textContrastMode?: 'normal' | 'high';
  timestamp: number;
}

// Helper function to find optimal text color
function findOptimalTextColor(backgroundColor: string): string {
  const candidates = ['#ffffff', '#000000'];
  
  let bestColor = '#000000';
  let bestContrast = 0;
  
  for (const candidate of candidates) {
    const contrast = calculateContrastRatio(backgroundColor, candidate);
    if (contrast > bestContrast) {
      bestContrast = contrast;
      bestColor = candidate;
    }
  }
  
  return bestColor;
}

export function NavbarEnhanced() {
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)
  const [paletteColors, setPaletteColors] = useState<{ background: string; text: string } | null>(null)
  
  // Get current page name for mobile display
  const currentPage = navItems.find(item => item.href === pathname)?.name || "MasterMind"

  // Load palette from sessionStorage and use last color for navbar
  useEffect(() => {
    const loadPaletteColors = () => {
      try {
        const storedPalette = sessionStorage.getItem('mastermind-palette');
        if (storedPalette) {
          const paletteData: MastermindPaletteData = JSON.parse(storedPalette);
          const lastColor = paletteData.palette[paletteData.palette.length - 1];
          
          if (lastColor) {
            const textColor = findOptimalTextColor(lastColor);
            setPaletteColors({
              background: lastColor,
              text: textColor
            });
            console.log('[navbar-enhanced]: Applied palette colors - bg:', lastColor, 'text:', textColor);
          }
        }
      } catch (error) {
        console.warn('[navbar-enhanced]: Error loading palette colors:', error);
        setPaletteColors(null);
      }
    };

    // Load initially
    loadPaletteColors();

    // Listen for palette updates from other parts of the app
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'mastermind-palette') {
        loadPaletteColors();
      }
    };

    // Listen for custom palette update events
    const handlePaletteUpdate = (event: MessageEvent) => {
      if (event.data?.type === 'palette-updated') {
        loadPaletteColors();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('message', handlePaletteUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('message', handlePaletteUpdate);
    };
  }, []);

  // Generate navbar styles based on palette
  const getNavbarStyles = () => {
    if (!paletteColors) {
      return {}; // Use default styles
    }

    return {
      backgroundColor: `${paletteColors.background}E6`, // 90% opacity
      borderColor: `${paletteColors.background}80`, // Semi-transparent border
    };
  };

  const getTextStyles = () => {
    if (!paletteColors) {
      return {}; // Use default styles
    }

    return {
      color: paletteColors.text,
    };
  };

  const getButtonStyles = () => {
    if (!paletteColors) {
      return {}; // Use default styles
    }

    return {
      color: paletteColors.text,
      '--hover-bg': `${paletteColors.text}15`, // Semi-transparent hover
      '--active-bg': `${paletteColors.text}20`, // Semi-transparent active
    } as React.CSSProperties;
  };

  return (
    <>
      {/* Dynamic styles for palette-based theming */}
      {paletteColors && (
        <style dangerouslySetInnerHTML={{
          __html: `
            .navbar-palette-button:hover {
              background-color: ${paletteColors.text}15 !important;
            }
            .navbar-palette-link:hover {
              background-color: ${paletteColors.text}15 !important;
            }
            .navbar-palette-active {
              background-color: ${paletteColors.text}20 !important;
            }
          `
        }} />
      )}
      
      <nav className="fixed top-0 z-50 w-full px-4 sm:px-6 lg:px-8 py-2">
        <div className="mx-auto max-w-4xl">
        <div 
          className={cn(
            "flex h-9 items-center gap-1 rounded-md border p-1 shadow-sm",
            !paletteColors && "bg-background" // Only apply bg-background when no palette
          )}
          style={getNavbarStyles()}
        >
          {/* Mobile menu trigger - only on mobile */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <div className="flex md:hidden items-center">
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-auto rounded-sm px-2 py-1",
                    !paletteColors && "hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
                    paletteColors && "navbar-palette-button"
                  )}
                  style={getButtonStyles()}
                >
                  <Menu className="h-4 w-4" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
            </div>

            {/* Desktop Navigation - centered, Mobile Current Page - centered */}
            <div className="flex flex-1 items-center justify-center">
              {/* Desktop nav items */}
              <nav className="hidden md:flex items-center gap-1">
                {navItems.filter(item => !item.hidden).map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "flex items-center rounded-sm px-2 py-1 text-sm font-medium outline-hidden select-none transition-colors",
                      !paletteColors && "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                      !paletteColors && pathname === item.href && "bg-accent text-accent-foreground",
                      paletteColors && "navbar-palette-link",
                      paletteColors && pathname === item.href && "navbar-palette-active"
                    )}
                    style={getTextStyles()}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
              
              {/* Mobile current page name - clickable */}
              <SheetTrigger asChild className="md:hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-auto rounded-sm px-2 py-1 text-sm font-medium",
                    !paletteColors && "hover:bg-accent hover:text-accent-foreground",
                    paletteColors && "navbar-palette-button"
                  )}
                  style={getButtonStyles()}
                >
                  {currentPage}
                </Button>
              </SheetTrigger>
            </div>

            <SheetContent side="left" className="w-[280px] sm:w-[350px]">
              <SheetHeader className="pb-4 border-b">
                <SheetTitle className="text-left font-semibold">Navigation</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col space-y-1 mt-6">
                {navItems.filter(item => !item.hidden).map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent",
                      pathname === item.href
                        ? "bg-accent text-accent-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
              
              {/* Mobile Auth Section */}
              <div className="mt-auto pt-8 border-t border-border">
                <div className="space-y-2">
                  <SignedOut>
                    <SignInButton mode="modal">
                      <Button variant="outline" className="w-full justify-start">
                        Sign in
                      </Button>
                    </SignInButton>
                    <SignUpButton mode="modal">
                      <Button className="w-full justify-start">
                        Sign up
                      </Button>
                    </SignUpButton>
                  </SignedOut>
                  <SignedIn>
                    <div className="flex items-center gap-3 px-3 py-2">
                      <UserButton 
                        afterSignOutUrl="/"
                        appearance={{
                          elements: {
                            avatarBox: "h-9 w-9"
                          }
                        }}
                      />
                      <span className="text-sm text-muted-foreground">Account</span>
                    </div>
                  </SignedIn>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Right Controls */}
          <div className="flex items-center gap-1">
            {/* Auth Controls */}
            <SignedIn>
              <UserButton 
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: "h-7 w-7"
                  }
                }}
              />
            </SignedIn>
            <SignedOut>
              <div className="hidden md:flex items-center gap-1">
                <SignInButton mode="modal">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={cn(
                      "h-auto rounded-sm px-2 py-1 text-sm font-medium",
                      paletteColors && "navbar-palette-button"
                    )}
                    style={getButtonStyles()}
                  >
                    Sign in
                  </Button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <Button 
                    size="sm"
                    className={cn(
                      "h-auto rounded-sm px-2 py-1 text-sm font-medium",
                      paletteColors && "navbar-palette-button"
                    )}
                    style={getButtonStyles()}
                  >
                    Sign up
                  </Button>
                </SignUpButton>
              </div>
            </SignedOut>
          </div>
          </div>
        </div>
      </nav>
    </>
  )
} 